import React, { useEffect, useMemo } from "react";
import { Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { MessengerProvider } from "@/packages/portal-messenger";
import setupAxiosInterceptors from "@/features/auth/services/axios/axios.interceptors";
import { PathNamekRoutes } from "@/router";
import { useAuthentication } from "@/features/auth/hooks/useAuthentication.hook";
import { LoginPage } from "@/features/auth/components/Login/Login.page";
import { useNetworkStatus } from "@/packages/orion/hooks/useNetworkStatus";
import { ResultBlocker, CenterLayout, LoadingModule } from "@/components";

import { isInIframe } from "./helpers/isInIframe";
import Logout from "./components/Logout.page";
import Unauthorized from "./components/Unauthorized.page";

function WrapperAuth({ children }: { children: React.ReactNode }) {
  const { isSignedIn } = useAuthentication();

  if (!isSignedIn) {
    return <LoadingModule title="conectando ..." />;
  }

  return <>{children}</>;
}

export function NamekAuthRoot() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnline = useNetworkStatus();

  const isHandlerPageBySelf = useMemo(() => {
    const handlerRoutes = [
      PathNamekRoutes.LOGIN,
      PathNamekRoutes.UNAUTHORIZED,
      PathNamekRoutes.LOGOUT,
    ];
    return handlerRoutes.some((path) => location.pathname === `/${path}`);
  }, [location]);

  useEffect(() => {
    const redirectToLogout = () => {
      if (!isInIframe()) {
        return navigate(`/${PathNamekRoutes.LOGIN}`, {
          replace: true,
        });
      }
      navigate(`/${PathNamekRoutes.LOGOUT}`, {
        replace: true,
      });
    };

    setupAxiosInterceptors(redirectToLogout);
  }, [navigate]);

  if (!isOnline) {
    return (
      <CenterLayout>
        <ResultBlocker status="offline" />
      </CenterLayout>
    );
  }

  return (
    <>
      <Routes>
        <Route Component={LoginPage} path={PathNamekRoutes.LOGIN} />
        <Route Component={Unauthorized} path={PathNamekRoutes.UNAUTHORIZED} />
        <Route Component={Logout} path={PathNamekRoutes.LOGOUT} />
      </Routes>
      {!isHandlerPageBySelf && (
        <>
          {isInIframe() ? (
            <MessengerProvider>
              <WrapperAuth>
                <Outlet />
              </WrapperAuth>
            </MessengerProvider>
          ) : (
            <WrapperAuth>
              <Outlet />
            </WrapperAuth>
          )}
        </>
      )}
    </>
  );
}
